import React, { useEffect, useMemo, useRef, useState } from 'react';
import { StatCell, StatCellAccent } from './StatCell';
import type { MetricKey as DefinitionKey } from '@/components/shared';
import type { UserInsightsResponse } from '../api/useUserInsights';

const STORAGE_KEY = 'userInsights.metrics';

type MetricKey =
  | 'total'
  | 'active'
  | 'newToday'
  | 'newThisWeek'
  | 'newThisMonth'
  | 'growthWoW'
  | 'growthMoM'
  | 'byAuthProvider.email'
  | 'byAuthProvider.google'
  | 'byAuthProvider.apple';

interface MetricDef {
  key: MetricKey;
  label: string;
  accent: StatCellAccent;
  info?: DefinitionKey;
  resolve: (data: UserInsightsResponse) => string | number;
}

const fmt = (n: number | null | undefined): string =>
  n === null || n === undefined ? '—' : Number(n).toLocaleString();

const fmtPct = (frac: number | null | undefined): string => {
  if (frac === null || frac === undefined) return '—';
  const pct = Math.round(frac * 100);
  return `${pct >= 0 ? '+' : ''}${pct}%`;
};

const ALL_METRICS: MetricDef[] = [
  { key: 'total',         label: 'Total Users',     accent: 'violet',  info: 'ui_total_users', resolve: (d) => fmt(d.metrics.total) },
  { key: 'active',        label: 'Active Accounts', accent: 'emerald', info: 'ui_active_accounts', resolve: (d) => fmt(d.metrics.active) },
  { key: 'newToday',      label: 'New Today',       accent: 'pink',    info: 'ui_new_today', resolve: (d) => fmt(d.metrics.newToday) },
  { key: 'newThisWeek',   label: 'New This Week',   accent: 'emerald', info: 'ui_new_week', resolve: (d) => fmt(d.metrics.newThisWeek) },
  { key: 'newThisMonth',  label: 'New This Month',  accent: 'violet',  info: 'ui_new_month', resolve: (d) => fmt(d.metrics.newThisMonth) },
  { key: 'growthWoW',     label: 'Growth (WoW)',    accent: 'pink',    info: 'ui_growth_wow', resolve: (d) => fmtPct(d.metrics.growthWoW) },
  { key: 'growthMoM',     label: 'Growth (MoM)',    accent: 'pink',    info: 'ui_growth_mom', resolve: (d) => fmtPct(d.metrics.growthMoM) },
  { key: 'byAuthProvider.email',  label: 'Email Signups',  accent: 'emerald', resolve: (d) => fmt(d.metrics.byAuthProvider?.email) },
  { key: 'byAuthProvider.google', label: 'Google Signups', accent: 'violet',  resolve: (d) => fmt(d.metrics.byAuthProvider?.google) },
  { key: 'byAuthProvider.apple',  label: 'Apple Signups',  accent: 'pink',    resolve: (d) => fmt(d.metrics.byAuthProvider?.apple) },
];

const DEFAULT_SELECTION: MetricKey[] = ['total', 'newThisWeek', 'newThisMonth'];

function loadSelection(): MetricKey[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SELECTION;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_SELECTION;
    const valid = parsed.filter((k): k is MetricKey =>
      ALL_METRICS.some((m) => m.key === k),
    );
    return valid.length > 0 ? valid : DEFAULT_SELECTION;
  } catch {
    return DEFAULT_SELECTION;
  }
}

export interface ConfigurableMetricsRowProps {
  data: UserInsightsResponse | null;
  className?: string;
}

export function ConfigurableMetricsRow({
  data,
  className,
}: ConfigurableMetricsRowProps): React.ReactElement {
  const [selection, setSelection] = useState<MetricKey[]>(() => loadSelection());
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
    } catch {
      /* ignore quota/serialization errors */
    }
  }, [selection]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (key: MetricKey) => {
    setSelection((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const visible = useMemo(
    () => ALL_METRICS.filter((m) => selection.includes(m.key)),
    [selection],
  );

  return (
    <div className={`flex items-stretch gap-3 ${className ?? ''}`}>
      <div className="flex flex-col sm:flex-row gap-3 flex-1">
        {visible.map((m) => (
          <StatCell
            key={m.key}
            label={m.label}
            value={data ? m.resolve(data) : '—'}
            accent={m.accent}
            info={m.info}
          />
        ))}
      </div>

      <div className="relative" ref={popoverRef}>
        <button
          type="button"
          aria-label="Configure metrics"
          onClick={() => setOpen((o) => !o)}
          className="h-full min-h-[64px] px-3 bg-gray-900/60 border border-gray-800 rounded-2xl text-gray-300 hover:text-white hover:bg-gray-800 transition-colors"
        >
          ⚙
        </button>
        {open && (
          <div className="absolute right-0 mt-2 w-56 z-20 bg-gray-900 border border-gray-700 rounded-xl shadow-xl p-3">
            <div className="text-[11px] uppercase tracking-widest text-gray-400 mb-2">
              Show metrics
            </div>
            <div className="flex flex-col gap-1.5">
              {ALL_METRICS.map((m) => (
                <label
                  key={m.key}
                  aria-label={m.label}
                  className="flex items-center gap-2 text-sm text-gray-200 cursor-pointer hover:text-white"
                >
                  <input
                    type="checkbox"
                    className="accent-violet-600"
                    checked={selection.includes(m.key)}
                    onChange={() => toggle(m.key)}
                  />
                  {m.label}
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ConfigurableMetricsRow;
